import {
  DocumentPlusIcon,
  FolderPlusIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { FolderIcon as FolderIconOutline } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { AnimatedModal } from "../AnimatedModal";
import {
  DataRoomFile,
  useDataRoomFolderBrowseQuery,
  DataRoomPermission,
  useSearchFilesQuery,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { Combobox } from "@headlessui/react";
import { classNames } from "../../utils/cn";
import Loading from "../Loading";
import { FileIcon } from "../FileIcon";
import { Breadcrumbs } from "./breadcrumbs";
import { FolderIcon, ListTreeIcon } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { dataRoomSelectors } from "@/src/store/data-room/selector";
import { AppState } from "@/src/store";
import { motion, AnimatePresence } from "framer-motion";

// Function to get recent searches from local storage
function getRecentSearches(key: string) {
  const searches = localStorage.getItem(key);
  return searches ? (JSON.parse(searches) as DataRoomFile[]) : [];
}

// Function to save a new search to local storage
function saveSearch(newSearch: DataRoomFile, key: string) {
  const searches = getRecentSearches(key);
  // Add new search at the beginning of the array
  searches.unshift(newSearch);
  // Keep only the 3 most recent searches
  const recentSearches = searches.slice(0, 3);
  localStorage.setItem(key, JSON.stringify(recentSearches));
}

const viewOptions = [
  {
    label: "Folder view",
    value: "folder",
    icon: <FolderIcon className="w-4 h-4" />,
  },
  {
    label: "Full tree view",
    value: "tree",
    icon: <ListTreeIcon className="w-4 h-4" />,
  },
];

export function DataRoomHeader(props: {
  folderId: string;
  permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  recentSearchesKey: string;
  dataRoomId: string;
  root: string;
  title?: string;
  selectedFolders?: string[];
  selectedFiles?: string[];
  setSelectedFolders?: (folders: string[]) => void;
  setSelectedFiles?: (files: string[]) => void;
  setTriggerMoveItems?: (trigger: boolean) => void;
  setTriggerDeleteItems?: (trigger: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();

  const dataRoomView = useSelector((state: AppState) =>
    dataRoomSelectors.dataRoomView(state, props.dataRoomId),
  );

  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState<"search" | "file" | "folder" | "">(
    "",
  );

  const [searchTerm, setSearchTerm] = useState("");

  const [recentSearches, setRecentSearches] = useState<DataRoomFile[]>([]);

  useEffect(() => {
    // Load recent searches from local storage on component mount
    const searches = getRecentSearches(props.recentSearchesKey);
    setRecentSearches(searches);
  }, []);

  // Example function to handle a new search
  // This should be called with the actual search result when a search is performed
  function handleNewSearch(searchResult: DataRoomFile) {
    saveSearch(searchResult, props.recentSearchesKey);
    // Optionally, update the state to immediately reflect the new search without reloading
    setRecentSearches(getRecentSearches(props.recentSearchesKey));
  }

  const client = useGqlClient();
  const location = useLocation();
  const { data, isLoading, error } = useDataRoomFolderBrowseQuery(
    client,
    {
      id: props.folderId,
    },
    {
      enabled: props.folderId !== "",
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  return (
    <div className="">
      <div className="">
        <div className="flex items-center justify-between"></div>

        <AnimatedModal
          open={openModal === "search"}
          onClose={() => {
            setOpenModal("");
          }}
          position="top"
          size="xl"
          padding="p-0"
        >
          <Combobox
            onChange={(v: any) => {
              if (v.shortcut) {
                return;
              }

              handleNewSearch(v);
              history.push(
                `/${props.root}/documents/folder/${props.folderId}/file/${v.id}`,
              );
            }}
          >
            <div className="flex flex-col h-full ">
              <div className="relative">
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                <Combobox.Input
                  className="h-12 w-full border-b select-none border-gray-100 rounded-t-md focus:outline-none focus:select-none focus:border-0 focus:border-b focus:border-gray-100 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                  placeholder="Search..."
                  onChange={(event) => {
                    setSearchTerm(event.currentTarget.value);
                  }}
                />
              </div>
              <div className="flex-1">
                <SearchResults
                  searchTerm={searchTerm}
                  folderId={props.folderId}
                  recentSearches={recentSearches}
                  dataRoomId={props.dataRoomId}
                  setOpenModal={setOpenModal}
                />
              </div>
            </div>
          </Combobox>
        </AnimatedModal>
      </div>
      <AnimatePresence>
        {dataRoomView !== "tree" && (
          <motion.div
            initial={{ height: 0, opacity: 0, marginBottom: 0 }}
            animate={{ height: "auto", opacity: 1, marginBottom: 12 }}
            exit={{ height: 0, opacity: 0, marginBottom: 0 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
              opacity: { duration: 0.2 },
            }}
            className="border-concrete-100 border-y z-20 px-2 w-full justify-between flex items-center overflow-hidden"
          >
            <Breadcrumbs
              folders={data ? data.dataRoomFolder.parentFolders : []}
              isLoading={isLoading}
              root={props.root}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface SearchResultsProps {
  folderId: string;
  searchTerm: string;
  recentSearches: DataRoomFile[];
  dataRoomId: string;
  setOpenModal: (modal: "search" | "file" | "folder" | "") => void;
}

let searchTimeout: NodeJS.Timeout | null = null;

const quickActions = [
  { name: "Add new file...", icon: DocumentPlusIcon, shortcut: "f", url: "#" },
  { name: "Add new folder...", icon: FolderPlusIcon, shortcut: "d", url: "#" },
];

function SearchResults(props: SearchResultsProps) {
  const history = useHistory();
  const [files, setFiles] = useState<DataRoomFile[]>([]);
  const { dealId } = useParams<{ dealId: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const client = useGqlClient();
  const searchQuery = useSearchFilesQuery(
    client,
    {
      query: searchTerm,
      dataRoomID: props.dataRoomId,
    },
    {
      enabled: searchTerm.length > 2,
    },
  );

  useEffect(() => {
    const term = props.searchTerm;

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    searchTimeout = setTimeout(() => {
      setSearchTerm(term);
    }, 1000);
  }, [props.searchTerm]);

  useEffect(() => {
    if (searchTerm.length < 2) {
      setFiles([]);
      return;
    }

    if (!searchQuery.data) {
      return;
    }

    setFiles(searchQuery.data.searchFiles as DataRoomFile[]);
  }, [searchTerm, searchQuery.data]);

  if (searchQuery.isError) {
    return <div>Something went wrong</div>;
  }

  if (searchQuery.isLoading) {
    return (
      <div className="h-full  justify-center items-center">
        <Loading />
      </div>
    );
  }

  if (searchTerm === "") {
    return (
      <Combobox.Options static className="max-h-80 scroll-py-2 overflow-y-auto">
        <h2 className="mb-2 mt-4 px-6 text-xs font-semibold text-gray-500">
          Recent searches
        </h2>
        {props.recentSearches.length === 0 ? (
          <p className="text-sm px-6 text-gray-400">No recent searches</p>
        ) : (
          <div className="px-2">
            {props.recentSearches.map((file, i) => {
              return (
                <Combobox.Option
                  key={i}
                  value={file}
                  className={({ active }) =>
                    classNames(
                      "flex cursor-default select-none items-center rounded-md px-3 py-2",
                      active ? "bg-gray-700 text-white" : "",
                    )
                  }
                >
                  {({ active }) => (
                    <>
                      <div className="bg-white rounded-full h-7 w-7 justify-center items-center flex p-1">
                        <FileIcon fileType={file.fileType} />
                      </div>
                      <span className="ml-3 flex-auto truncate text-sm">
                        {file.parentFolders
                          .map((pf, i) => {
                            return `${pf.name === "" ? "Home" : pf.name
                              }${" / "}`;
                          })
                          .reverse()}
                        <span className="font-bold">{file.name}</span>
                      </span>
                      {active && (
                        <span
                          className="ml-3 flex-none text-gray-100 text-sm cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            props.setOpenModal("");
                            setTimeout(() => {
                              history.push(
                                `/deal/${dealId}/documents/folder/${file.folderId}/file/${file.id}`,
                              );
                            }, 10);
                          }}
                        >
                          Jump to...
                        </span>
                      )}
                    </>
                  )}
                </Combobox.Option>
              );
            })}
          </div>
        )}
        <WriteAccessOnly folderId={props.folderId}>
          <div className="w-full h-px mt-4 bg-gray-100"></div>
        </WriteAccessOnly>

        <WriteAccessOnly folderId={props.folderId}>
          <li className="p-2 list-none">
            <h2 className="sr-only">Quick actions</h2>
            <ul className=" text-sm text-gray-700">
              {quickActions.map((action) => (
                <Combobox.Option
                  key={action.shortcut}
                  value={action}
                  className={({ active }) =>
                    classNames(
                      "flex cursor-default select-none items-center rounded-md px-3 py-2",
                      active ? "bg-gray-200" : "",
                    )
                  }
                >
                  {({ active }) => (
                    <>
                      <action.icon
                        className={classNames(
                          "h-6 w-6 flex-none",
                          "text-gray-400",
                        )}
                        aria-hidden="true"
                      />
                      <span className="ml-3 flex-auto truncate">
                        {action.name}
                      </span>
                      <span
                        className={classNames(
                          "ml-3 flex-none text-xs font-semibold",
                          "text-gray-400",
                        )}
                      >
                        <kbd className="font-sans">⌘</kbd>
                        <kbd className="font-sans">{action.shortcut}</kbd>
                      </span>
                    </>
                  )}
                </Combobox.Option>
              ))}
            </ul>
          </li>
        </WriteAccessOnly>
      </Combobox.Options>
    );
  }

  if (!searchQuery.data || files.length === 0) {
    return (
      <div className="flex py-8 h-full justify-center items-center flex-col">
        <FolderIconOutline className="w-6 h-6 text-gray-400" />
        <p className="text-gray-600 text-sm">
          We couldn't find any files with that search term
        </p>
      </div>
    );
  }

  return (
    <>
      <Combobox.Options
        static
        className="max-h-80 scroll-py-2 divide-y divide-gray-100 overflow-y-auto"
      >
        <li className="p-2">
          <ul
            className="text-sm text-gray-700"
            key={files.map((f) => f.id).join("-")}
          >
            {files.map((file) => (
              <Combobox.Option
                key={file.id}
                value={file}
                className={({ active }) =>
                  classNames(
                    "flex cursor-default select-none items-center rounded-md px-3 py-2",
                    active ? "bg-gray-700 text-white" : "",
                  )
                }
              >
                {({ active }) => (
                  <>
                    <div className="bg-white rounded-full h-7 w-7 justify-center items-center flex p-1">
                      <FileIcon fileType={file.fileType} />
                    </div>
                    <span className="ml-3 flex-auto truncate">
                      {file.parentFolders
                        .map((pf, i) => {
                          return `${pf.name === "" ? "Home" : pf.name}${" / "}`;
                        })
                        .reverse()}
                      <span className="font-bold">{file.name}</span>
                    </span>
                    {active && (
                      <span
                        className="ml-3 flex-none text-gray-100 text-sm cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.setOpenModal("");
                          setTimeout(() => {
                            history.push(
                              `/deal/${dealId}/documents/folder/${file.folderId}/file/${file.id}`,
                            );
                          }, 0);
                        }}
                      >
                        Jump to...
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </ul>
        </li>
      </Combobox.Options>
    </>
  );
}

function ReadAccessOnly(props: {
  children: React.ReactNode;
  folderId: string;
}) {
  const client = useGqlClient();
  const dataRoomFolderQuery = useDataRoomFolderBrowseQuery(
    client,
    {
      id: props.folderId,
    },
    {
      enabled: props.folderId !== "",
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (
    dataRoomFolderQuery.isPending ||
    !dataRoomFolderQuery.data ||
    dataRoomFolderQuery.error
  ) {
    return null;
  }

  if (!dataRoomFolderQuery.data.dataRoomFolder.hasFolderWriteAccess) {
    return <>{props.children}</>;
  }

  return null;
}

export function WriteAccessOnly(props: {
  children: React.ReactNode;
  folderId: string;
}) {
  const client = useGqlClient();
  const dataRoomFolderQuery = useDataRoomFolderBrowseQuery(
    client,
    {
      id: props.folderId,
    },
    {
      enabled: props.folderId !== "",
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (
    dataRoomFolderQuery.isPending ||
    !dataRoomFolderQuery.data ||
    dataRoomFolderQuery.error
  ) {
    return null;
  }

  if (dataRoomFolderQuery.data.dataRoomFolder.hasFolderWriteAccess) {
    return <>{props.children}</>;
  }

  return null;
}
